import { useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';

import type { DashboardConfig } from 'src/types/dashboard';
import type { OrgTreeState } from 'src/components/common/OrgTree/types';
import {
  chatbotPrimeData,
  chatbotVisibilityState,
} from 'src/components/chatbot/atoms';
import { ChatbotState } from 'src/components/chatbot/constants';
import { dashboardContentLoading } from 'src/components/dashboard/DashboardPage/atom';
import { getChatbotType } from 'src/components/dashboard/DashboardPage/helper';

const useDashboardChatbot = (
  orgTreeState: OrgTreeState,
  dashboardConfig?: DashboardConfig,
) => {
  const [chatbotVisibility] = useAtom(chatbotVisibilityState);
  const [contentLoading] = useAtom(dashboardContentLoading);
  const resetChatbotPreChat = useResetAtom(chatbotPrimeData('preChat'));
  const resetChatbotFakeChat = useResetAtom(chatbotPrimeData('fakeChat'));

  useEffect(() => {
    resetChatbotPreChat();
    resetChatbotFakeChat();
    // stringify array dependency to prevent false positives on equality check
  }, [orgTreeState.selectedOrgUnits, dashboardConfig]);

  const chatbotRoomId = useMemo(() => {
    if (
      dashboardConfig &&
      chatbotVisibility === ChatbotState.VISIBLE &&
      !contentLoading &&
      orgTreeState.selectedOrgUnits.length > 0
    ) {
      const orgUnitIdString = orgTreeState.selectedOrgUnits.reduce(
        (acc: string, { id, org_unit_id }) =>
          acc ? `${acc}--${id}-${org_unit_id}` : `${id}-${org_unit_id}`,
        '',
      );
      return `dashboard-${dashboardConfig.id}-orgUnit-${orgUnitIdString}`;
    }
    return undefined;
  }, [dashboardConfig, orgTreeState, chatbotVisibility, contentLoading]);

  const chatbotType = useMemo(() => {
    if (!contentLoading && dashboardConfig) {
      return getChatbotType(dashboardConfig, orgTreeState);
    }
    return undefined;
  }, [dashboardConfig, orgTreeState, contentLoading]);

  return {
    chatbotRoomId,
    chatbotType,
  };
};

export default useDashboardChatbot;
